/**
 * Typed API client. Replaces the legacy src/app/static/app-auth.js.
 *
 * Reads the optional bearer token from localStorage["dashboard_token"]
 * (set via TokenGate or the Settings modal) and attaches it to all API requests.
 * When DASHBOARD_TOKEN is set on the server, all /api/* routes require it.
 */

import type {
  ActivateCompanyResponse,
  AddCompanyResponse,
  CompanyInfo,
  Config,
  DeactivateCompanyResponse,
  Job,
  JobDescriptionResponse,
  ScanError,
  ScanRun,
  ScanStatus,
  StatsPoint,
  SkippedCompany,
  UnresolvedCompany,
} from "./types";

const TOKEN_KEY = "dashboard_token";

export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) || "";
}

export function setToken(token: string): void {
  const trimmed = token.trim();
  if (trimmed) localStorage.setItem(TOKEN_KEY, trimmed);
  else localStorage.removeItem(TOKEN_KEY);
}

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

async function getJSON<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new ApiError(url, res.status, await res.text());
  return (await res.json()) as T;
}

async function postJSON<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = await res.text();
    try {
      detail = JSON.parse(detail).detail || detail;
    } catch {
      /* keep raw */
    }
    throw new ApiError(url, res.status, String(detail));
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(
    public readonly url: string,
    public readonly status: number,
    public readonly detail: string,
  ) {
    super(`API ${status} ${url}: ${detail}`);
    this.name = "ApiError";
  }
}

// --- Jobs ---

export function fetchJobs(opts: {
  filter?: string;
  q?: string;
  runId?: string | null;
} = {}): Promise<Job[]> {
  const params = new URLSearchParams();
  params.set("filter", opts.filter ?? "all");
  if (opts.q) params.set("q", opts.q);
  if (opts.runId) params.set("run_id", opts.runId);
  return getJSON<Job[]>(`/api/jobs?${params.toString()}`);
}

export function fetchJobDescription(job: {
  id: string;
  url: string;
  ats: string;
}): Promise<JobDescriptionResponse> {
  const params = new URLSearchParams({
    id: job.id,
    url: job.url,
    ats: job.ats,
  });
  return getJSON<JobDescriptionResponse>(`/api/job-description?${params.toString()}`);
}

export function updateShortlist(id: string, shortlisted: 0 | 1) {
  return postJSON<{ success: boolean; id: string; is_shortlisted: number }>(
    "/api/shortlist",
    { id, shortlisted },
  );
}

export function updateJobStatus(id: string, status: string) {
  return postJSON<{ success: boolean; id: string; status: string }>(
    "/api/update-status",
    { id, status },
  );
}

export function updateJobNotes(id: string, notes: string) {
  return postJSON<{ success: boolean; id: string }>(`/api/update-notes`, { id, notes });
}

export function archiveJob(id: string) {
  return postJSON<{ success: boolean; id: string; is_archived: number }>("/api/archive-job", { id });
}

// --- Config ---

export function fetchConfig(): Promise<Config> {
  return getJSON<Config>("/api/config");
}

export function saveConfig(config: Config) {
  return postJSON<{ success: boolean }>("/api/config", config);
}

export function updateWatchlist(company: string, action: "star" | "unstar") {
  return postJSON<{ success: boolean; watchlist: string[] }>(`/api/watchlist`, {
    company,
    action,
  });
}

export function addCompany(query: string) {
  return postJSON<AddCompanyResponse>("/api/add-company", { query });
}

export function deactivateCompany(company: string) {
  return postJSON<DeactivateCompanyResponse>("/api/deactivate-company", { company });
}

export function pauseCompany(company: string) {
  return postJSON<{ success: boolean; company: string; inactive: boolean; message: string }>(
    "/api/pause-company",
    { company },
  );
}

export function activateCompany(company: string) {
  return postJSON<ActivateCompanyResponse>("/api/activate-company", { company });
}

export function fetchUnresolvedCompanies() {
  return getJSON<UnresolvedCompany[]>("/api/unresolved-companies");
}

export function fetchSkippedCompanies() {
  return getJSON<SkippedCompany[]>("/api/skipped-companies");
}

// --- Scan ---

export function fetchScanStatus(): Promise<ScanStatus> {
  return getJSON<ScanStatus>("/api/scan-status");
}

export function triggerScan() {
  return postJSON<{ status: string }>("/api/scan", {});
}

export function fetchScanHistory(): Promise<ScanRun[]> {
  return getJSON<ScanRun[]>("/api/scan-history");
}

export function fetchScanErrors(): Promise<ScanError[]> {
  return getJSON<ScanError[]>("/api/scan-errors");
}

export function fixScanError(companyName: string) {
  return postJSON<{ success: boolean; message: string; company: { name: string; ats: string; slug: string } }>(
    "/api/fix-scan-error",
    { company_name: companyName },
  );
}

// --- Stats ---

export function fetchStats(): Promise<StatsPoint[]> {
  // Backend returns array of [date_str, count] tuples.
  return getJSON<StatsPoint[]>("/api/stats");
}

// --- Company info ---

export function fetchCompanyInfo(company: string): Promise<CompanyInfo> {
  const params = new URLSearchParams({ company });
  return getJSON<CompanyInfo>(`/api/company-info?${params.toString()}`);
}

// --- Env ---

export function fetchEnv(): Promise<Record<string, string>> {
  return getJSON<Record<string, string>>("/api/env");
}

export function saveEnv(env: Record<string, string>) {
  return postJSON<{ success: boolean }>("/api/env", env);
}
